import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../../_core/_services/auth.service';
import { Router } from '@angular/router';
import { MiniUserDto } from '../../../_core/_model/users';
import { Subscription } from 'rxjs';
import { UserNotificationsService } from '../../../_core/_services/user-notifications.service';
import { ACCOUNT_USER_ROLES, STAFF_ROLES, UserRole, hasAnyRole } from '../../../_core/_model/user-role';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header implements OnInit, OnDestroy {
  @Input() page: string = "Dashboard";
  showNotice: boolean = false;

  userData: MiniUserDto | null = null;

  notificationUnread = 0;
  private sub = new Subscription();
  private notificationUserId: string | null = null;

  constructor(
    private auth: AuthService,
    private router: Router,
    private notifications: UserNotificationsService,
  ) {}

  ngOnInit(): void {
    this.sub.add(this.auth.currentUser$.subscribe(u => {
      this.userData = u;
      this.showNotice = !!u && !u.kyc;

      if (u && this.notificationUserId !== u.id) {
        this.notificationUserId = u.id;
        this.notifications.refreshSummary();
      }

      if (!u) {
        this.notificationUserId = null;
        this.notifications.reset();
      }
    }));

    this.sub.add(this.notifications.summary$.subscribe(summary => {
      this.notificationUnread = summary.unread || 0;
    }));

    this.auth.getCurrentUser().subscribe();
  }
  
  logout() {
    this.auth.logout().subscribe(() => {
      this.router.navigate(['/'], { replaceUrl: true });
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  hideNotice(): boolean {
    return !hasAnyRole(this.userData?.role, STAFF_ROLES);
  }


  get canUseAccountFunctions(): boolean {
    return hasAnyRole(this.userData?.role, ACCOUNT_USER_ROLES);
  }

  get isComplianceReviewerOnly(): boolean {
    return this.userData?.role === 'Compliance';
  }

  get isStaffUser(): boolean {
    return hasAnyRole(this.userData?.role, STAFF_ROLES);
  }

  get staffRoleLabel(): UserRole | null {
    return this.isStaffUser
      ? this.userData?.role as UserRole
      : null;
  }

  get canReviewAdditionalDocuments(): boolean {
    return ['Admin', 'SuperAdmin', 'Compliance'].includes(this.userData?.role || '');
  }

  get documentReviewUrl(): string {
    return this.userData?.role === 'Compliance'
      ? '/review/additional-documents'
      : '/admin/additional-documents';
  }

  get initials(): string {
    const f = this.userData?.firstName?.trim()?.[0] ?? '';
    const l = this.userData?.lastName?.trim()?.[0] ?? '';
    return (f + l).toUpperCase() || '--';
  }

  get displayName(): string {
    const f = this.userData?.firstName?.trim() ?? '';
    const l = this.userData?.lastName?.trim() ?? '';
    return (f || l) ? `${f} ${l}`.trim() : 'Account';
  }
}
